import { BookingData } from "@src/BookingTools/App/AppDataProvider/types";

type BookingDataKey = keyof BookingData;

/**
 * Asserts that a BookingData object is of a particular subtype.
 *
 * This is used to validate in each Step that fields which are required for the
 * current step have been correctly completed earlier in the flow.
 *
 * (I haven't been able to find a way to guarantee that the requiredFields array
 * matches the required fields on Type, because one exists at run-time and the
 * other only at design-time. So for now that's for the developer to manage.)
 *
 * @example
 * type SpecificStepBookingData = PartRequired<BookingData, "field1" | "field2">
 *
 * function SpecificStep() {
 *   const { bookingData } = appData;  // field1 and field2 may be undefined
 *   requireBookingDataFields<SpecificStepBookingData>(bookingData, [
 *     "field1",
 *     "field2",
 *   ]);
 *   const { field1, field2 } = bookingData;  // no longer possibly undefined
 *   ...
 * }
 *
 * @param bookingData a BookingData object we want to check has certain fields
 * @param requiredFields the names of the fields we want to ensure are defined
 */
export function requireBookingDataFields<Type extends BookingData>(
  bookingData: BookingData,
  requiredFields: BookingDataKey[]
): asserts bookingData is Type {
  requiredFields.forEach((fieldName) => {
    if (typeof bookingData[fieldName] === "undefined") {
      throw new Error(`Booking data is missing a required field: ${fieldName}`);
    }
  });
}
